import { useState } from "react";
import { Building2, Home, Hammer, Key, Search, Store, CheckCircle2 } from "lucide-react";
import { PageHero } from "./About";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PARTNERS = [
  { icon: Building2, label: "Estate agents" }, { icon: Key, label: "Letting agents" },
  { icon: Hammer, label: "Property developers" }, { icon: Home, label: "Landlords" },
  { icon: Search, label: "Property sourcing businesses" }, { icon: Store, label: "Commercial property agents" },
];

export default function PartnerWithUs() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", partner_type: "", message: "", consent: false, honeypot: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/partner", form);
      setDone(true);
      toast.success("Thank you — our team will be in touch.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHero kicker="Partner with us" title="Reach motivated buyers and tenants" subtitle="Join Homio's network of trusted property partners and receive qualified enquiries." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl font-bold text-[#111827]">Who we work with</h2>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {PARTNERS.map((p) => (
              <div key={p.label} className="flex items-center gap-3 bg-white rounded-xl border border-border p-4">
                <div className="w-10 h-10 rounded-full bg-[#C81D35]/10 flex items-center justify-center"><p.icon className="w-5 h-5 text-[#C81D35]" /></div>
                <span className="text-sm font-semibold text-[#111827]">{p.label}</span>
              </div>
            ))}
          </div>
          <ul className="mt-8 space-y-3">
            {["Qualified buyer and tenant enquiries","No long-term tie-ins","A premium showcase for your listings","Simple referral arrangements"].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[#111827]/80"><CheckCircle2 className="w-5 h-5 text-[#C5A059]" /> {t}</li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-sm p-8">
          {done ? (
            <div className="text-center py-10" data-testid="partner-success">
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
              <h3 className="mt-4 font-display text-2xl font-bold">Request received</h3>
              <p className="mt-2 text-muted-foreground">Thank you for your interest in partnering with Homio. Our team will review your request and be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="partner-form">
              <h3 className="font-display text-2xl font-bold text-[#111827]">Become a property partner</h3>
              <input type="text" tabIndex={-1} className="hidden" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} aria-hidden />
              <div className="space-y-1.5"><Label>Company name *</Label><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="partner-company" /></div>
              <div className="space-y-1.5"><Label>Contact name *</Label><Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="partner-contact" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="partner-email" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="partner-phone" /></div>
              </div>
              <div className="space-y-1.5"><Label>Partner type</Label>
                <Select value={form.partner_type} onValueChange={(v) => set("partner_type", v)}>
                  <SelectTrigger data-testid="partner-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{PARTNERS.map((p) => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Message</Label><Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="partner-message" /></div>
              <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="partner-consent" />
                <span>I consent to Homio contacting me about partnership opportunities.</span>
              </label>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#C81D35] hover:bg-[#a5162b]" data-testid="partner-submit">{busy ? "Submitting..." : "Submit request"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
